import React from 'react';
import {
  DialogContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AppDialog, { AppDialogHeader } from '../common/AppDialog';
import {
  Person as PersonIcon,
} from '@mui/icons-material';
import { Customer, CreateCustomerRequest, UpdateCustomerRequest } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import CustomerForm from './CustomerForm';

interface CustomerDialogProps {
  open: boolean;
  customer?: Customer | null;
  onClose: () => void;
  onSubmit: (data: CreateCustomerRequest | UpdateCustomerRequest) => Promise<void>;
  isLoading?: boolean;
}

const CustomerDialog: React.FC<CustomerDialogProps> = ({
  open,
  customer,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleSubmit = async (data: CreateCustomerRequest | UpdateCustomerRequest) => {
    await onSubmit(data);
    onClose();
  };

  return (
    <AppDialog presentation="form" footerInside closeDisabled={isLoading}
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? false : "md"}
      fullWidth={!isMobile}
      fullScreen={isMobile}
      scroll="body"
      PaperProps={{
        sx: {
          minHeight: isMobile ? '100vh' : '400px',
          maxHeight: isMobile ? '100vh' : '90vh',
          margin: isMobile ? 0 : 2,
          width: isMobile ? '100vw' : 'auto',
          borderRadius: isMobile ? 0 : 2,
          position: 'relative',
          ...(isMobile && {
            display: 'flex',
            flexDirection: 'column',
          }),
        }
      }}
    >
      <AppDialogHeader icon={<PersonIcon />} subtitle={customer ? customer.name || `${customer.firstName} ${customer.lastName}`.trim() : undefined}>
        {customer ? t('customers.editCustomer') : t('customers.createCustomer')}
      </AppDialogHeader>

      <DialogContent 
        dividers={!isMobile}
        sx={{ 
          flex: isMobile ? 1 : 'none',
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 2,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <CustomerForm
          customer={customer}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isLoading={isLoading}
        />
      </DialogContent>
    </AppDialog>
  );
};

export default CustomerDialog;
